import React, {
  memo, useCallback, useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { AlertService } from '@common/services/alert';
import { useThunkDispatch } from '@hooks/redux';
import updateProfile from '@modules/core/actions/update-profile';
import { setTheme as applyBrandTheme } from '../utils/color';
import type { StoreState } from '../types/store';
import { applyDocumentTheme } from './apply-document-theme';
import {
  DEFAULT_THEME,
  ResolvedTheme,
  SYSTEM_THEME_QUERY,
  ThemePreference,
} from './constants';
import {
  getSystemTheme,
  themePreferenceFromApi,
  themePreferenceToApi,
} from './resolve-theme';
import { getStoredTheme, setStoredTheme } from './storage';
import { ThemeContext } from './theme-context';

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: ThemePreference;
}

function ThemeProviderComponent({
  children,
  defaultTheme = DEFAULT_THEME,
}: ThemeProviderProps) {
  const { t } = useTranslation();
  const thunkDispatch = useThunkDispatch();
  const [theme, setThemeState] = useState<ThemePreference>(() => {
    if (typeof window === 'undefined') return defaultTheme;
    return getStoredTheme();
  });
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(getSystemTheme);

  const organisationTheme = useSelector(
    (state: StoreState) => state.organisation?.selected?.theme ?? null,
  );
  const organisationId = useSelector(
    (state: StoreState) => state.organisation?.selected?.id ?? null,
  );
  const userColorScheme = useSelector(
    (state: StoreState) => state.loggedUser?.user?.color_scheme ?? null,
  );

  const setTheme = useCallback((next: ThemePreference) => {
    setThemeState(next);
    setStoredTheme(next);

    if (!organisationId) return;
    void (async () => {
      try {
        await thunkDispatch(updateProfile({
          color_scheme: themePreferenceToApi(next),
        }));
      } catch {
        AlertService.error(t('core:theme_save_error'));
      }
    })();
  }, [organisationId, t, thunkDispatch]);

  useEffect(() => {
    const apiTheme = themePreferenceFromApi(userColorScheme);
    if (!apiTheme) return;

    setThemeState(apiTheme);
    // Keep localStorage as the boot-time cache so the anti-flash script can
    // apply the server preference before the user request completes next time.
    setStoredTheme(apiTheme);
  }, [userColorScheme]);

  const resolvedTheme: ResolvedTheme = theme === 'system' ? systemTheme : theme;

  useEffect(() => {
    if (theme !== 'system' || typeof window.matchMedia !== 'function') return undefined;

    const mediaQuery = window.matchMedia(SYSTEM_THEME_QUERY);
    const handleChange = () => setSystemTheme(mediaQuery.matches ? 'dark' : 'light');
    handleChange();

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, [theme]);

  useEffect(() => {
    applyDocumentTheme(resolvedTheme);
  }, [resolvedTheme]);

  useEffect(() => {
    if (!organisationTheme) return;
    void applyBrandTheme(organisationTheme, resolvedTheme);
  }, [organisationTheme, resolvedTheme]);

  const value = useMemo(() => ({
    theme,
    setTheme,
    resolvedTheme,
  }), [resolvedTheme, setTheme, theme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export const ThemeProvider = memo(ThemeProviderComponent);
